import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ApiPermissions, IntegrationApp } from '@proto/integrationapp';
import { InstallPbxIntegrationAppRequest, InstallPbxIntegrationAppRequest_ApiKey, InstallPbxIntegrationAppResponse_Outcome } from '@proto/odoo/integrations';
import { firstValueFrom } from 'rxjs';
import { Subscription } from 'src/app/subscription/subscription.model';
import { IntegrationAppsService } from '../../integration-apps.service';
import { ToastService } from 'src/app/toast.service';
import { I18NextService } from 'angular-i18next';
import { pcDate } from 'src/app/date.service';
import { PermissionsWithSecret, InstallationDialogContentType } from '../../integration-apps.types';

@Component({
  selector: 'app-integration-apps-dialog',
  templateUrl: './integration-apps-dialog.component.html',
  standalone: false
})
export class IntegrationAppsDialogComponent implements OnChanges {

  private integrationService = inject(IntegrationAppsService);
  private toastService = inject(ToastService);
  private i18Next = inject(I18NextService);

  // toggle dialog visibility
  @Input() showDialog: boolean = false;
  @Output() showDialogChange = new EventEmitter<boolean>();

  // string used to switch screens
  @Input() contentType: InstallationDialogContentType = '';
  @Output() contentTypeChange = new EventEmitter<InstallationDialogContentType>();

  @Input() selectedAppId?: string;
  @Input() subscriptionList?: Subscription[];
  @Input() preselectedSubscription?: Subscription;
  @Input() previousApiPermissions?: ApiPermissions[];
  @Input() isUpgrade: boolean = false;
  @Input() failedInstallationApplianceIds = new Set<string>();
 
  @Output() uninstall = new EventEmitter(); 
  @Output() installationSuccess = new EventEmitter();

  public subscriptionsInitial: Subscription[] = [];
  public subscriptionsToDisplay: Subscription[] = [];

  public selectedSubscription: Subscription;

  public integrationApp: IntegrationApp;
  public integrationAppVersion: number;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['preselectedSubscription'] && this.preselectedSubscription) {
      this.selectedSubscription = this.preselectedSubscription;
      this.fetchAppDetails();
    }
    if (changes['subscriptionList'] && this.subscriptionList) {
      this.subscriptionsInitial = this.subscriptionList.filter(sub =>
        sub.end_date ? pcDate(sub.end_date).isAfter(Date.now()) : true
      );
      this.subscriptionsToDisplay = [...this.subscriptionsInitial]
    }
  }

  hideTheDialog() {
    this.contentType = 'selectSubscription';
    this.contentTypeChange.emit(this.contentType);
    this.showDialog = false;
    this.showDialogChange.emit(this.showDialog);
  }

  onBack(backFrom: string) {
    if (backFrom === 'showResources') {
      this.contentType = 'selectSubscription';
      this.contentTypeChange.emit(this.contentType);
    } else if (backFrom === 'showApiKeySecrets') {
      this.contentType = 'showResources';
      this.contentTypeChange.emit(this.contentType);
    }
  }

  filter(event: string) {
    this.subscriptionsToDisplay = this.subscriptionsInitial.filter(sub => sub.instance_name.toLowerCase().includes(event.toLowerCase()) || 
      sub.customer_name.toLowerCase().includes(event.toLowerCase()));
  }

  async subSelected(subscription: Subscription) {
    this.selectedSubscription = subscription;
    if (!this.integrationApp) {
      await this.fetchAppDetails();
    }
    if (this.integrationApp) {
      this.contentType = 'showResources';
      this.contentTypeChange.emit(this.contentType);
    }
  }

  async fetchAppDetails() {
    try {
      const integrationAppDetailsResponse = await firstValueFrom(this.integrationService.getAppDetails(this.selectedAppId));
    
      if (integrationAppDetailsResponse.app && integrationAppDetailsResponse.appVersion) {
        this.integrationApp = integrationAppDetailsResponse.app;
        this.integrationAppVersion = integrationAppDetailsResponse.appVersion;
      } else {
        this.toastService.error(this.i18Next.t('integration_apps_app_details_error'), 'error');
        return;
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_load_resources_error'), 'error');
      return;
    }
  }

  get isRetry(): boolean {
    return !this.isUpgrade && 
      !!this.selectedSubscription &&
      this.failedInstallationApplianceIds.has(
        String(this.selectedSubscription.appliance_id)
      );
  }

  confirmUninstall() {
    this.uninstall.emit();
  }

  showSecretScreen(): boolean {
    return (this.integrationApp?.apiPermissions ?? []).some(
      permission => permission.scopes.length > 0
    );
  }

  onResourceImportConfirmed() {
    if (!this.isRetry && this.showSecretScreen()) {
      this.contentType = 'showApiKeySecrets';
      this.contentTypeChange.emit(this.contentType);
      return;
    }

    // we assume that a first (failed) attempt still provided the hashed secret which we MUST NOT regenerate on a retry!
    const apiKeys = this.isRetry
      ? (this.integrationApp.apiPermissions ?? []).map(permission =>
        InstallPbxIntegrationAppRequest_ApiKey.create({
          identifier: permission.identifier
        })
      )
    : undefined;

    this.installApp(apiKeys);
  }

  async hashPermissionSecrets(permissionsWithSecret: PermissionsWithSecret[]) {
    const apiKeys: InstallPbxIntegrationAppRequest_ApiKey[] = [];

    for (const permission of permissionsWithSecret) {
      const apiKey: Partial<InstallPbxIntegrationAppRequest_ApiKey> = {
        identifier: permission.identifier
      };

      if (permission.generatedSecret !== undefined) {
        const secretHash = await this.sha256Hex(permission.generatedSecret);
        apiKey.secretHash = `sha256:${secretHash}`;
      }

      apiKeys.push(
        InstallPbxIntegrationAppRequest_ApiKey.create(apiKey)
      );
    }
    await this.installApp(apiKeys);
  }

  private async sha256Hex(value: string): Promise<string> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  async installApp(apiKeys?: InstallPbxIntegrationAppRequest_ApiKey[]) {
    const entry = InstallPbxIntegrationAppRequest.create({
      applianceId: parseInt(this.selectedSubscription.appliance_id),
      integrationAppUuid: this.integrationApp.uuid,
      integrationAppVersion: this.integrationAppVersion,
      apiKeys: apiKeys
    });
    
    try {
      const installResponse = await firstValueFrom(this.integrationService.installApp(entry));

      if (installResponse.outcome === InstallPbxIntegrationAppResponse_Outcome.OUTCOME_SUCCESS) {
        this.hideTheDialog();
        this.toastService.success(this.i18Next.t('integration_apps_toast_installation_success'), 'success');
        this.installationSuccess.emit();
      }
      if (installResponse.outcome === InstallPbxIntegrationAppResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('integration_apps_toast_installation_error'), 'error');
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_start_installation_error'), 'error');
    }

  }

}
